import type { ReactNode } from "react";

type IllustrationVariant =
  | "quickstart"
  | "basics"
  | "reference"
  | "auth"
  | "journey"
  | "workflow"
  | "practices";

const illustrations: Record<IllustrationVariant, ReactNode> = {
  quickstart: (
    <svg viewBox="0 0 120 80" fill="none" aria-hidden className="h-full w-full">
      <rect x="18" y="28" width="44" height="34" rx="6" className="fill-primary/20" />
      <rect x="58" y="18" width="44" height="44" rx="6" className="fill-primary/35" />
      <path
        d="M30 45h20M40 38v14"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        className="text-primary"
      />
      <circle cx="80" cy="40" r="10" className="fill-background stroke-primary" strokeWidth="2" />
    </svg>
  ),
  basics: (
    <svg viewBox="0 0 120 80" fill="none" aria-hidden className="h-full w-full">
      <rect x="24" y="14" width="52" height="52" rx="6" className="fill-primary/25" />
      <path
        d="M36 30h28M36 40h20M36 50h24"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-primary/70"
      />
      <circle cx="86" cy="52" r="14" className="fill-primary/15 stroke-primary/40" strokeWidth="2" />
      <path
        d="M80 52h12M86 46v12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-primary"
      />
    </svg>
  ),
  reference: (
    <svg viewBox="0 0 120 80" fill="none" aria-hidden className="h-full w-full">
      <rect x="20" y="20" width="80" height="44" rx="8" className="fill-primary/15 stroke-primary/30" strokeWidth="1.5" />
      <path
        d="M32 34h20M32 44h36M32 54h28"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-primary/60"
      />
      <path
        d="M68 30l8 6-8 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary"
      />
    </svg>
  ),
  auth: (
    <svg viewBox="0 0 120 80" fill="none" aria-hidden className="h-full w-full">
      <path
        d="M60 16c-8 8-20 10-20 22v8c0 14 9 22 20 26 11-4 20-12 20-26v-8c0-12-12-14-20-22Z"
        className="fill-primary/20 stroke-primary/50"
        strokeWidth="1.5"
      />
      <circle cx="60" cy="42" r="6" className="fill-primary" />
      <rect x="44" y="54" width="32" height="10" rx="5" className="fill-primary/30" />
    </svg>
  ),
  journey: (
    <svg viewBox="0 0 120 80" fill="none" aria-hidden className="h-full w-full">
      <circle cx="28" cy="40" r="10" className="fill-primary/30" />
      <circle cx="60" cy="40" r="10" className="fill-primary/50" />
      <circle cx="92" cy="40" r="10" className="fill-primary" />
      <path
        d="M38 40h14M72 40h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-primary/60"
      />
    </svg>
  ),
  workflow: (
    <svg viewBox="0 0 120 80" fill="none" aria-hidden className="h-full w-full">
      <rect x="22" y="18" width="76" height="48" rx="8" className="fill-primary/15 stroke-primary/35" strokeWidth="1.5" />
      <rect x="32" y="28" width="24" height="8" rx="2" className="fill-primary/40" />
      <rect x="32" y="42" width="56" height="6" rx="2" className="fill-primary/25" />
      <rect x="32" y="52" width="40" height="6" rx="2" className="fill-primary/25" />
    </svg>
  ),
  practices: (
    <svg viewBox="0 0 120 80" fill="none" aria-hidden className="h-full w-full">
      <path
        d="M60 14l36 18v22c0 16-16 26-36 30C40 84 24 74 24 54V32L60 14Z"
        className="fill-primary/20 stroke-primary/45"
        strokeWidth="1.5"
      />
      <path
        d="M48 40l8 8 16-18"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary"
      />
    </svg>
  ),
};

type CardIllustrationProps = {
  variant: IllustrationVariant;
};

export function CardIllustration({ variant }: CardIllustrationProps) {
  return <div className="flex h-full items-center justify-center p-6">{illustrations[variant]}</div>;
}

export type { IllustrationVariant };
